import React, { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import {
  FaMapMarkerAlt,
  FaPencilAlt,
  FaExclamationTriangle,
  FaInfoCircle,
  FaCheck,
} from "react-icons/fa";
import { Button } from "../../components/ui/button";
import { DataTable } from "../../components/DataTable";
import { AlertTriangle, ArrowUpDown, ChevronRight } from "lucide-react";
import { GoChecklist, GoLog, GoPlusCircle } from "react-icons/go";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import Modal from "../../components/Modal";
import { LuScanQrCode } from "react-icons/lu";
import MaterialSelect from "../../components/Materialselect";
import { POCService, transactionService } from "../../services";
import { checkinService } from "../../services/checkinService";
import useUserStore from "../../stores/userStore";
import useMaterialStore from "../../stores/materialStore";
import ShortenedText from "../../components/ShortenedText";
import { toast } from "react-toastify";

const columns = [
  {
    accessorKey: "transactionId",
    header: "No.",
    cell: ({ row }) => <span>{row.getValue("transactionId")}</span>,
  },
  {
    accessorKey: "selectedMaterial",
    header: "Material Category",
    cell: ({ row }) => <span>{row.getValue("selectedMaterial")}</span>,
  },
  {
    accessorKey: "quantity",
    header: "Quantity",
    cell: ({ row }) => <span>{row.getValue("quantity")}</span>,
  },
];

const POCCollector = () => {
  const [scannedPOC, setScannedPOC] = useState(null);
  const [error, setError] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const scannerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [materialCategory, setMaterialCategory] = useState("");
  const [quantity, setQuantity] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [transactionToEdit, setTransactionToEdit] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewPOCModal, setViewPOCModal] = useState(false);
  const [logoutModal, setLogoutModal] = useState(false);
  const [submittedTransactions, setSubmittedTransactions] = useState([]);
  const [showSubmittedTransactionsModal, setShowSubmittedTransactionsModal] =
    useState(false);

  const handleAddClick = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setMaterialCategory("");
    setQuantity("");
  };

  const handleSubmit = () => {
    const newTransaction = {
      transactionId: `${transactions.length + 1}`, // Will always be the next available number
      selectedMaterial,
      quantity: quantity,
    };
    setTransactions((prev) => [...prev, newTransaction]);
    setIsSubmitDisabled(false);
    handleModalClose();
  };

  const handleSubmitClick = () => {
    setIsConfirmationModalOpen(true);
  };

  const handleConfirmationSubmit = async () => {
    if (transactions.length === 0) {
      alert("No transactions to submit.");
      return;
    }

    if (!scannedPOC) {
      alert("No POC scanned. Please scan a POC QR code first.");
      return;
    }

    setIsSubmitDisabled(true);

    try {
      setLoading(true);
      const { pocId } = scannedPOC;
      const collectorId = useUserStore.getState().userId; // Using CollectorId instead
      const materials = useMaterialStore.getState().materials;

      console.log("Transactions to submit:", transactions);
      console.log("Available materials:", materials);

      const payload = {
        Transactions: transactions
          .map((tx) => {
            console.log(`Processing Material: ${tx.selectedMaterial}`);

            let material = materials.find(
              (m) => m.category === tx.selectedMaterial
            );

            if (!material) {
              console.error(`Material '${tx.selectedMaterial}' not found.`);
              return null;
            }

            let selectedMaterial = material;

            if (material?.sub_material?.length) {
              const subMaterial = material.sub_material.find(
                (sub) => sub.category === tx.selectedMaterial
              );
              if (subMaterial) {
                selectedMaterial = subMaterial;
              }
            }

            console.log(
              `Final Material ID: ${selectedMaterial._id}, Points: ${selectedMaterial.point}`
            );

            return {
              PocId: pocId,
              CollectorId: collectorId,
              MaterialId: selectedMaterial._id,
              TransactionUnit: selectedMaterial.unit || "kg",
              TransactionPoint: selectedMaterial.point || 0,
              Quantity: Number(tx.quantity),
            };
          })
          .filter((tx) => tx !== null),
      };

      console.log("Final Payload:", JSON.stringify(payload, null, 2));

      const response = await transactionService.addTransactions(payload);
      console.log("Submission successful:", response);

      // Save submitted transactions
      setSubmittedTransactions(response.Data || []);
      setShowSubmittedTransactionsModal(true);

      setTransactions([]);
      setScannedPOC(null);
      setIsConfirmationModalOpen(false);
    } catch (error) {
      console.error("Failed to submit transactions:", error);
      toast.error("❌ Transaction submission failed. Please try again", {
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
      setIsSubmitDisabled(true);
    }
  };

  const handleConfirmationModalClose = () => {
    setIsConfirmationModalOpen(false);
  };

  const handleDeleteClick = (transactionId) => {
    setTransactionToDelete(transactionId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (transactionToDelete) {
      setTransactions((prevTransactions) => {
        // Filter out the deleted transaction
        const updated = prevTransactions
          .filter(
            (transaction) => transaction.transactionId !== transactionToDelete
          )
          // Re-index transactionId starting from 1
          .map((transaction, index) => ({
            ...transaction,
            transactionId: `${index + 1}`,
          }));

        return updated;
      });

      setShowDeleteModal(false);
      setTransactionToDelete(null);
    }
  };

  const actions = (row) => [
    {
      label: "Edit details",
      icon: <FaPencilAlt />,
      onClick: () => {
        setTransactionToEdit({
          transactionId: row.transactionId,
          selectedMaterial: row.selectedMaterial,
          quantity: row.quantity,
        });
        setIsOpen(true);
      },
    },
    {
      label: "Delete",
      icon: <FaExclamationTriangle />,
      variant: "dropRed",
      onClick: () => handleDeleteClick(row.transactionId),
    },
  ];

  const handleCheckin = async (scannedPOCData) => {
    try {
      setLoading(true);
      const { userId } = useUserStore.getState(); // Get userId from Zustand

      if (!scannedPOCData) {
        setError("POC data is missing. Check-in cannot proceed.");
        return;
      }

      const checkinData = {
        PocId: scannedPOCData.pocId,
        EntityId: userId,
      };

      console.log("Sending check-in data:", checkinData);

      const response = await checkinService.addCheckin(checkinData);

      if (response?.Data) {
        console.log("Check-in successful:", response);

        // Extract check-in time from API response
        const checkInTime = response.Data.create_date;

        // Store check-in state in localStorage
        const storedData = { ...scannedPOCData, checkInTime };
        localStorage.setItem("checkedInPOC", JSON.stringify(storedData));

        setScannedPOC(storedData); // Update state
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Check-in failed:", error);
      setError("Check-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleScanSuccess = async (decodedText) => {
    try {
      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 100));
      stopScanning();

      const pocId = decodedText.split("/").pop();
      if (!pocId) {
        setError("Invalid POC QR Code. Please scan a valid POC QR code.");
        setLoading(false);
        return;
      }

      try {
        const response = await POCService.getPOCDetails(pocId);
        if (response?.Data?.length > 0) {
          const pocData = response.Data[0];
          const scannedPOCData = {
            pocId: pocData._id,
            name: pocData.name,
            address1: pocData.address_line1,
            address2: pocData.address_line2,
            postcode: pocData.postcode,
            city: pocData.city,
            state: pocData.state,
            country: pocData.country,
          };

          setScannedPOC(scannedPOCData);
          setError(null);
          await handleCheckin(scannedPOCData);
        } else {
          setError("POC details not found. Please try again.");
        }
      } catch (apiError) {
        setError("Failed to fetch POC details. Please try again.");
      }
    } catch (err) {
      setError("Invalid QR Code format. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleScanError = (err) => {
    console.warn("Scan Error:", err);
  };

  const handleCameraChange = async (newCameraId) => {
    try {
      if (scannerRef.current && isScanning) {
        await scannerRef.current.stop();
        setSelectedCamera(newCameraId);
        await scannerRef.current.start(
          newCameraId,
          { fps: 10, qrbox: { width: 250, height: 250 } },
          handleScanSuccess,
          handleScanError
        );
      }
    } catch (err) {
      setError("Failed to switch camera. Please try again.");
    }
  };

  const startScanning = async () => {
    setError(null);
    setIsScanning(true);
    try {
      const devices = await Html5Qrcode.getCameras();
      if (devices.length === 0) {
        setError("No camera found. Please connect a camera.");
        return;
      }

      setCameras(devices);
      const cameraId = devices.at(-1).id;
      setSelectedCamera(cameraId);

      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode("qr-scanner");
      }

      await scannerRef.current.start(
        cameraId,
        { fps: 10, qrbox: { width: 250, height: 250 } },
        handleScanSuccess,
        handleScanError
      );
    } catch (err) {
      setError(
        "Failed to access the camera. Check browser permissions and settings."
      );
      setIsScanning(false);
    }
  };

  const stopScanning = () => {
    if (scannerRef.current) {
      scannerRef.current
        .stop()
        .then(() => {
          scannerRef.current.clear();
          setIsScanning(false);
        })
        .catch((err) => {
          console.error("Failed to stop scanning:", err);
        });
    }
  };

  useEffect(() => {
    return () => {
      if (scannerRef.current && scannerRef.current.getState() === 2) {
        // Check if scanner is running
        scannerRef.current.stop().catch((err) => {
          console.error("Failed to stop scanner on unmount:", err);
        });
      }
      setCameras([]);
      setSelectedCamera("");
    };
  }, []);

  useEffect(() => {
    const storedPOC = localStorage.getItem("checkedInPOC");
    if (storedPOC) {
      setScannedPOC(JSON.parse(storedPOC)); // Restore the check-in state
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("checkedInPOC"); // Remove check-in state
    setScannedPOC(null); // Reset state
    window.location.reload(); // Refresh the page
  };
  const materials = useMaterialStore((state) => state.materials);

  const selectedMaterialObject = React.useMemo(() => {
    const parent = materials.find((m) => m.category === selectedMaterial);
    if (!parent) return null;

    if (parent.sub_material?.length) {
      const sub = parent.sub_material.find(
        (s) => s.category === selectedMaterial
      );
      return sub || parent;
    }

    return parent;
  }, [selectedMaterial, materials]);

  const getMaterialName = (materialId) => {
    const found = materials.find((m) => m._id === materialId);
    return found ? found.category : materialId;
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <div className="flex-1 flex flex-col">
        <div className="px-4 sm:px-8 mt-2">
          {scannedPOC && (
            <div className="bg-white shadow p-4 rounded-lg flex items-center w-full sm:w-1/2">
              <FaMapMarkerAlt className="text-gray-600 text-4xl mr-3" />
              <div>
                <p
                  onClick={() => setLogoutModal(true)}
                  className="text-sm cursor-pointer text-green-600"
                >
                  Successfully Checked-In &gt;
                </p>
                <h3 className="text-lg font-semibold text-gray-800">
                  {scannedPOC.name}
                </h3>
              </div>
              <FaInfoCircle
                className="text-blue-500 text-2xl cursor-pointer ml-auto"
                onClick={() => setViewPOCModal(true)}
              />
            </div>
          )}
        </div>

        <div className="flex-1 p-4 sm:p-8">
          <div className="bg-white shadow rounded-lg p-4 sm:p-6 text-sm w-full h-full mx-auto px-4 sm:px-6 flex flex-col">
            <div className="w-full flex flex-col sm:flex-row justify-between items-center sm:items-start">
              <h2 className="text-xl font-bold text-gray-800 mb-2 sm:mb-0 text-center sm:text-left">
                {scannedPOC ? "Transaction List" : "POC Check-in"}
              </h2>

              {scannedPOC && (
                <div className="flex flex-col sm:flex-row gap-2 ml-auto sm:ml-0 items-center sm:items-start w-full sm:w-auto">
                  <div className="w-full sm:w-auto flex justify-center">
                    <Button
                      variant={isSubmitDisabled ? "outline" : "hollowGreen"}
                      disabled={isSubmitDisabled}
                      onClick={handleSubmitClick}
                    >
                      Submit
                    </Button>
                  </div>
                  <div className="w-full sm:w-auto flex justify-center">
                    <Button onClick={handleAddClick}>
                      <GoPlusCircle className="" /> Add
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <hr className="mb-6 mt-4 border-gray-200" />

            <div
              className={`flex-1 flex flex-col items-center ${
                !scannedPOC ? "justify-center" : "justify-start w-full px-4"
              }`}
            >
              {loading ? (
                <div>
                  <div className="animate-spin mx-auto rounded-full h-20 w-20 font-bold border-t-4 border-green-500"></div>
                  <p className="mt-3 font-bold text-gray-800 text-lg">
                    Processing QR Code...
                  </p>
                </div>
              ) : !scannedPOC ? (
                <>
                  <LuScanQrCode className="text-gray-500 text-6xl mb-4" />
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">
                    You are not checked in
                  </h2>
                  <p className="text-gray-600 mb-6 text-center">
                    To get started, simply click the 'Scan QR Code' button below
                    and scan your code to check in.
                  </p>
                  <Button onClick={isScanning ? stopScanning : startScanning}>
                    {isScanning ? "Stop Scanning" : "Scan QR Code"}
                  </Button>
                  {isScanning && (
                    <div className="mt-4 w-full max-w-md">
                      <div className="flex flex-col gap-4">
                        <div id="qr-scanner"></div>
                        <Select
                          value={selectedCamera}
                          onValueChange={handleCameraChange}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select camera" />
                          </SelectTrigger>
                          <SelectContent>
                            {cameras.map((camera) => (
                              <SelectItem key={camera.id} value={camera.id}>
                                {camera.label || `Camera ${camera.id}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full">
                  <DataTable
                    columns={columns}
                    data={transactions}
                    actions={actions}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Receipt modal */}
          <Modal
            isOpen={showSubmittedTransactionsModal}
            onClose={() => setShowSubmittedTransactionsModal(false)}
          >
            <div className="max-h-[80vh] w-auto overflow-y-auto text-sm sm:text-base">
              {/* Icon and title */}
              <div className="flex flex-col items-center mb-4">
                <div className="p-4 bg-teal-100 rounded-full mb-3">
                  <GoLog className="text-teal-500 text-2xl" />
                </div>
                <h2 className="text-xl font-bold mb-2">
                  Submission Successful!
                </h2>
              </div>

              {submittedTransactions.length > 0 && (
                <div className="p-4 bg-teal-50  text-sm  rounded-md mb-4">
                  <div className="grid grid-cols-12 gap-y-2">
                    <p className="col-span-4 font-semibold text-left">Date:</p>
                    <p className="col-span-8 text-left">
                      {new Date(submittedTransactions[0].date).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}

              <ul className="space-y-4">
                {submittedTransactions.map((tx) => (
                  <li
                    key={tx._id}
                    className="p-4 bg-gray-50 border text-sm border-gray-100 rounded-md shadow-sm"
                  >
                    <div className="grid grid-cols-12 gap-y-2">
                      <p className="col-span-4 font-semibold text-left">
                        Transaction ID:
                      </p>
                      <p className="col-span-8 break-all text-left">{tx._id}</p>

                      <p className="col-span-4 font-semibold text-left">
                        Material ID:
                      </p>
                      <p className="col-span-8 break-all text-left">
                        {getMaterialName(tx.material)}
                      </p>

                      <p className="col-span-4 font-semibold text-left">
                        Quantity:
                      </p>
                      <p className="col-span-8 text-left">
                        {tx.quantity} {tx.transaction_unit}
                      </p>

                      <p className="col-span-4 font-semibold text-left">
                        Total Points:
                      </p>
                      <p className="col-span-8 text-left">
                        {tx.transaction_point * tx.quantity}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-6 flex justify-end">
                <Button
                  onClick={() => setShowSubmittedTransactionsModal(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          </Modal>

          {/* Add Transaction Modal */}
          <Modal isOpen={isModalOpen} onClose={handleModalClose}>
            <div className="">
              <div className="flex justify-between items-center border-b pb-3">
                <h2 className="text-lg font-semibold text-gray-900">
                  Add New Transaction
                </h2>
              </div>
              <hr className="mb-6 border-gray-200" />

              <div className="space-y-4">
                <div>
                  <Label
                    htmlFor="material-category"
                    className="block text-left"
                  >
                    Material Category
                  </Label>
                  <MaterialSelect
                    value={selectedMaterial}
                    onChange={setSelectedMaterial}
                  />
                </div>

                <div>
                  <Label htmlFor="quantity" className="block text-left">
                    Quantity
                  </Label>
                  <div className="relative">
                    <Input
                      id="quantity"
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      placeholder="Enter quantity"
                    />
                    <span className="absolute right-3 mr-4 top-2 text-gray-500">
                      {selectedMaterialObject?.unit || "unit"}
                    </span>
                  </div>
                </div>
              </div>

              <hr className="mt-6 mb-4 border-gray-200" />

              <div className="flex justify-between">
                <Button variant="outline" onClick={handleModalClose}>
                  Cancel
                </Button>
                <Button onClick={handleSubmit}>Add</Button>
              </div>
            </div>
          </Modal>

          {/* Confirmation Modal */}
          <Modal
            isOpen={isConfirmationModalOpen}
            onClose={handleConfirmationModalClose}
            icon={
              <div className="bg-teal-100 rounded-full p-3 inline-flex items-center justify-center">
                <GoChecklist className="text-teal-500 w-6 h-6" />
              </div>
            }
            title="Confirm Submission"
          >
            <div className="mt-0">
              <p className="text-gray-600 mb-6">
                You're about to submit all your pending transactions. Please
                make sure everything is correct before proceeding.
              </p>
              <hr className="mb-6 border-gray-200" />
              <div className="flex justify-between gap-2">
                <Button
                  variant="outline"
                  onClick={handleConfirmationModalClose}
                >
                  Cancel
                </Button>
                <Button onClick={handleConfirmationSubmit} disabled={loading}>
                  {loading ? (
                    <span className="flex items-center gap-2">
                      Submitting...
                    </span>
                  ) : (
                    "Submit Now"
                  )}
                </Button>
              </div>
            </div>
          </Modal>

          {/* Delete Modal */}
          <Modal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
          >
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                Delete Transaction
              </h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this transaction? This action is
                NOT reversible and will result in permanent loss of this
                transaction.
              </p>
              <hr className="mb-6 border-gray-200" />
              <div className="flex justify-between gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleConfirmDelete}>
                  Delete
                </Button>
              </div>
            </div>
          </Modal>

          {/* Edit modal */}
          <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                Edit Transaction
              </h2>
              <hr className="mb-6 border-gray-200" />

              <div className="space-y-4">
                <div>
                  <Label
                    htmlFor="material-category"
                    className="block text-left"
                  >
                    Material Category
                  </Label>
                  <MaterialSelect
                    value={transactionToEdit?.selectedMaterial || ""}
                    onChange={(value) => {
                      setTransactionToEdit((prev) => ({
                        ...prev,
                        selectedMaterial: value, // Ensure the material is updated correctly
                      }));
                    }}
                  />
                </div>

                <div>
                  <Label htmlFor="quantity" className="block text-left">
                    Quantity
                  </Label>
                  <div className="relative">
                    <Input
                      id="quantity"
                      type="number"
                      value={
                        transactionToEdit?.quantity?.replace(" KG", "") || ""
                      }
                      onChange={(e) =>
                        setTransactionToEdit((prev) => ({
                          ...prev,
                          quantity: `${e.target.value} KG`,
                        }))
                      }
                      placeholder="Enter quantity"
                    />
                    <span className="absolute right-3 mr-4 top-2 text-gray-500">
                      KG
                    </span>
                  </div>
                </div>
              </div>

              <hr className="mt-6 mb-4 border-gray-200" />

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setIsOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    const updatedTransactions = transactions.map(
                      (transaction) =>
                        transaction.transactionId ===
                        transactionToEdit.transactionId
                          ? transactionToEdit
                          : transaction
                    );
                    setTransactions(updatedTransactions);
                    setIsOpen(false); // Close the modal
                  }}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </Modal>

          {error && (
            <div className="bg-red-100 text-red-700 p-4 mt-4 rounded-lg">
              {error}
            </div>
          )}
        </div>

        {/* POC Details Modal */}
        <Modal isOpen={viewPOCModal} onClose={() => setViewPOCModal(false)}>
          {/* Header */}
          <div className="flex justify-between items-center border-b pb-3">
            <h2 className="text-lg font-semibold text-gray-900">POC Details</h2>
          </div>

          {/* Top Section: POC ID, Check-In Time, Status */}
          <div className="flex flex-wrap gap-x-4 gap-y-3 py-4 text-sm w-full justify-between">
            <div>
              <p className="text-gray-500 text-left">POC ID</p>
              <p className="font-medium text-gray-900">
                <ShortenedText text={scannedPOC?.pocId} />
              </p>
            </div>
            <div>
              <p className="text-gray-500 text-left">Check-In Time</p>
              {scannedPOC?.checkInTime ? (
                <div className="font-medium text-gray-900">
                  <p>{new Date(scannedPOC.checkInTime).toLocaleDateString()}</p>
                  <p>{new Date(scannedPOC.checkInTime).toLocaleTimeString()}</p>
                </div>
              ) : (
                <p className="text-gray-900">N/A</p>
              )}
            </div>

            <div>
              <p className="text-gray-500 text-left">Status</p>
              <span
                className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium cursor-pointer"
                onClick={() => {
                  setLogoutModal(true);
                }}
              >
                Successfully Checked-in &gt;
              </span>
            </div>
          </div>

          {/* POC Information */}
          <div className="text-sm border-t pt-4">
            <div className="flex flex-col gap-y-2">
              {[
                { label: "POC Name", value: scannedPOC?.name },
                { label: "Address 1", value: scannedPOC?.address1 },
                { label: "Address 2", value: scannedPOC?.address2 },
                { label: "Postcode", value: scannedPOC?.postcode },
                { label: "City", value: scannedPOC?.city },
                { label: "State", value: scannedPOC?.state },
                { label: "Country", value: scannedPOC?.country },
              ].map((item, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-1 sm:grid-cols-2 text-left px-3 py-2 rounded ${
                    index % 2 === 0 ? "bg-gray-50" : "bg-white"
                  }`}
                >
                  <span className="text-gray-500">{item.label}</span>
                  <span className="text-gray-900 font-medium">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row justify-between gap-3 mt-6">
            <Button
              variant="outline"
              className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              onClick={() => setViewPOCModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant={"destructive"}
              onClick={() => {
                setLogoutModal(true);
              }}
            >
              Check Out
            </Button>
          </div>
        </Modal>

        {/* Logout modal */}
        <Modal
          isOpen={logoutModal}
          onClose={() => {
            setLogoutModal(false);
          }}
          title="Ready to Check Out?"
          description={
            <>
              Looks like you're done!
              <br />
              Confirm your checkout to complete your visit.
            </>
          }
          icon={
            <div className="bg-red-100 rounded-full p-3 inline-flex items-center justify-center">
              <AlertTriangle className="text-red-500 w-6 h-6" />
            </div>
          }
        >
          <div className="flex justify-between gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setLogoutModal(false);
              }}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleLogout}>
              Yes, Check out now.
            </Button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default POCCollector;
